import { useMemo, useEffect } from 'react'
import { Environment, Lightformer } from '@react-three/drei'
import { useThree } from '@react-three/fiber'
import { ErrorBoundary } from '../components/logic/ErrorBoundary.jsx'
import React from 'react'
import { debugLog } from '../utils/debug.js'

const SHADOW_CONFIG = {
  bias: -0.0001,
  normalBias: 0.05,
  mapSize: 2048,
}

// Magic numbers that were found experimentally
const LIGHT_INTENSITY_FACTOR_DIRECTIONAL = 0.0025
const LIGHT_INTENSITY_FACTOR_POINT = 0.01
const LIGHT_INTENSITY_FACTOR_ENVIRONMENT = .48
const LIGHT_INTENSITY_FACTOR_BACKGROUND = 4

function processSceneLights(scene)
{
  let hasLights = false
  scene.traverse(obj =>
  {
    if (obj.isLight)
    {
      debugLog('[useSceneLighting] Processing light: ' + obj.name)
      hasLights = true
      // Store original intensity to prevent compounding multiplication on re-renders/hot-reloads
      if (obj.userData.originalIntensity === undefined)
      {
        obj.userData.originalIntensity = obj.intensity
      }

      if (obj.shadow)
      {
        obj.castShadow = true
        obj.shadow.bias = SHADOW_CONFIG.bias
        obj.shadow.normalBias = SHADOW_CONFIG.normalBias
        obj.shadow.mapSize.width = SHADOW_CONFIG.mapSize
        obj.shadow.mapSize.height = SHADOW_CONFIG.mapSize
      }
      if (obj.isDirectionalLight)
      {
        obj.intensity = obj.userData.originalIntensity * LIGHT_INTENSITY_FACTOR_DIRECTIONAL
      }
      else if (obj.isPointLight)
      {
        obj.intensity = obj.userData.originalIntensity * LIGHT_INTENSITY_FACTOR_POINT
      }
    }
  })
  return hasLights
}


function createEnvironmentElement(worldConfig, hasSceneBackground, hasLights)
{
  let intensity = worldConfig?.environmentIntensity ?? 1

  if (worldConfig?.environmentTexture)
  {
    if (intensity > 1) intensity *= LIGHT_INTENSITY_FACTOR_ENVIRONMENT
    return (
      <>
        <Environment
          files={'/assets/' + worldConfig.environmentTexture}
          background={worldConfig.background !== undefined ? worldConfig.background : true}
          environmentIntensity={intensity}
        />
      </>
    )
  }

  if (worldConfig?.backgroundColor)
  {
    return (
      <>
        <ambientLight intensity={LIGHT_INTENSITY_FACTOR_BACKGROUND * intensity} color={worldConfig.backgroundColor} />
        <color attach="background" args={worldConfig.backgroundColor} />
      </>
    )
  }

  return null
}


/**
 * Hook for scene lighting based on world configuration.
 */
export function useSceneLighting(scene, sceneManager, overrideWorldConfig = null)
{
  // 1. Resolve World Config
  const worldConfig = useMemo(() =>
  {
    let config = overrideWorldConfig

    if (!config && !sceneManager && scene)
    {
      scene.traverse((obj) =>
      {
        if (config) return
        if (obj.userData?.siteProperties && obj.userData.worldConfig)
        {
          const userData = obj.userData
          let bgColor = userData.background_color
          if (bgColor && typeof bgColor === 'object' && '0' in bgColor)
          {
            bgColor = [ bgColor[ 0 ], bgColor[ 1 ], bgColor[ 2 ] ]
          }
          config = {
            environmentTexture: userData.environment_texture,
            background: userData.background,
            backgroundColor: bgColor,
            environmentIntensity: userData.environment_intensity,
          }
        }
      })
    }

    if (!config && sceneManager)
    {
      config = sceneManager.getSiteData()?.worldConfig ?? null
    }

    return config
  }, [ scene, sceneManager, overrideWorldConfig ])

  // 2. Set Background Intensity explicitly (updates the visual background brightness)
  useEffect(() =>
  {
    if (scene)
    {
      const intensity = worldConfig?.environmentIntensity ?? 1
      scene.backgroundIntensity = intensity > 1 ? intensity * LIGHT_INTENSITY_FACTOR_ENVIRONMENT : intensity
    }
  }, [ scene, worldConfig ])

  // 3. Process Lights & Rendering
  return useMemo(() =>
  {
    const hasLights = processSceneLights(scene)

    return (
      <ErrorBoundary FallbackComponent={<></>}>
        {createEnvironmentElement(worldConfig, Boolean(scene.background), hasLights)}
      </ErrorBoundary>
    )
  }, [ scene, worldConfig ])
}

import { useFrame } from '@react-three/fiber'

export function SceneLighting({ scene, sceneManager, worldConfig, mixer })
{
  useFrame((state, delta) =>
  {
    if (mixer) mixer.update(delta)
  })
  return useSceneLighting(scene, sceneManager, worldConfig)
}
